import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { AuthModel, getAuth } from '../../modules/auth';
import { Result } from '../models/Result';
import { NavigateFunction } from 'react-router-dom';
import { refreshtoken } from '../../modules/auth/core/_requests';
import { format } from 'date-fns';

// The API stores dates without a zone, so a Date sent as UTC ISO lands a day early before 05:30 IST.
// Send every Date in a JSON payload as local wall-clock time instead.
const localizeDates = (value: any): any => {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : format(value, "yyyy-MM-dd'T'HH:mm:ss");
  }
  if (Array.isArray(value)) {
    return value.map(localizeDates);
  }
  if (value && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, localizeDates(v)]));
  }
  return value;
};

const onRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const auth = getAuth();

  if (auth && auth.data.token) {
    config.headers.Authorization = `Bearer ${auth.data.token}`;
  }

  config.data = localizeDates(config.data);

  return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
  const contentType = String(response.headers['content-type'] ?? '');

  // ✅ Only transform if it's JSON
  if (contentType.includes('application/json') && typeof response.data === 'object') {
    response.data = transformNullToUndefined(response.data);
  }

  return response;
};

const onResponseError = async (
  error: AxiosError | Error,
  navigate: NavigateFunction,
  saveAuth: (auth: AuthModel | undefined) => void,
  logout: () => void
): Promise<Result | undefined> => {
  if (axios.isAxiosError(error)) {
    const { status } = (error.response as AxiosResponse) ?? {};

    switch (status) {
      case 400: {
        // Bad Request
        return Promise.reject(error.response?.data);
      }
      case 401: {
        // "Login required"
        let exception = error.response?.data?.exception;
        if (exception === 'Invalid Refresh Token.' || exception === 'Authentication Failed.') {
          RedirectToLogin(navigate, logout);
          return Promise.resolve(undefined);
        }

        // "Login required"
        const result = await RefreshAccessToken(navigate, saveAuth, logout);

        if (result) {
          // Retry the original request with the new access token
          const originalRequest = error.config;
          if (originalRequest != null) {
            originalRequest.headers.Authorization = `Bearer ${result.data.token}`;
            return axios(originalRequest);
          }
        } else {
          RedirectToLogin(navigate, logout);
        }

        return Promise.resolve(undefined);
      }
      case 403: {
        // "Permission denied"
        navigate('error/access-denied');
        return Promise.resolve(undefined);
      }
      case 404: {
        // "Invalid request"
        break;
      }
      case 500: {
        // "Server error"
        return Promise.resolve(error.response?.data);
      }
      default: {
        // "Unknown error occurred"
        RedirectToLogin(navigate, logout);
        return Promise.resolve(undefined);
      }
    }

    // if(status === 401){
    //     removeAuth()
    // }
  }

  return Promise.reject(error);
};

export function setupInterceptorsTo(
  axiosInstance: AxiosInstance,
  navigate: NavigateFunction,
  saveAuth: (auth: AuthModel | undefined) => void,
  logout: () => void
): AxiosInstance {
  axiosInstance.interceptors.request.use(onRequest, onRequestError);
  axiosInstance.interceptors.response.use(onResponse, (error) => {
    return onResponseError(error, navigate, saveAuth, logout);
  });
  return axiosInstance;
}

// Function to redirect to the login page
export const RedirectToLogin = (navigate: NavigateFunction, logout: () => void) => {
  logout();
  navigate('/auth');
};

// Function to refresh the access token using the refresh token
export const RefreshAccessToken = async (
  navigate: NavigateFunction,
  saveAuth: (auth: AuthModel | undefined) => void,
  logout: () => void
) => {
  const auth = getAuth();
  let refreshToken = auth?.data.refreshToken;

  if (!auth || !refreshToken) {
    RedirectToLogin(navigate, logout);
    return null;
  }

  try {
    const { data: response } = await refreshtoken(auth.data.token, refreshToken);
    saveAuth(response);
    return response;
  } catch (error) {
    // Handle errors that occur during the token refresh process
    RedirectToLogin(navigate, logout);
  }
};

const transformNullToUndefined = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(transformNullToUndefined);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        value === null ? undefined : transformNullToUndefined(value),
      ])
    );
  }
  return obj;
};
